import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import i18n, { normLang } from "@/i18n";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Profile language wins over device detection: apply the account's preferred_lang on load/login.
const applyUserLang = (u) => {
  const pl = u && u.preferred_lang;
  if (pl && (pl === "fr" || pl === "en") && normLang(i18n.language) !== pl) i18n.changeLanguage(pl);
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const token = localStorage.getItem("twp_token");
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      applyUserLang(data);
    } catch {
      localStorage.removeItem("twp_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.requires_2fa) return { requires_2fa: true, temp_token: data.temp_token, email_verification: !!data.email_verification };
    localStorage.setItem("twp_token", data.token);
    setUser(data.user);
    applyUserLang(data.user);
    return data.user;
  };

  const twofaLogin = async (temp_token, code) => {
    const { data } = await api.post("/auth/2fa/login", { temp_token, code });
    localStorage.setItem("twp_token", data.token);
    setUser(data.user);
    applyUserLang(data.user);
    return data.user;
  };

  const register = async (name, email, password, refCode, extra = {}) => {
    const ref = refCode || localStorage.getItem("twp_ref_code") || undefined;
    const { data } = await api.post("/auth/register", { name, email, password, ...extra, ref_code: ref, lang: (localStorage.getItem("twp_lang") || "en").split("-")[0] === "fr" ? "fr" : "en" });
    localStorage.removeItem("twp_ref_code");
    localStorage.setItem("twp_token", data.token);
    setUser(data.user);
    applyUserLang(data.user);
    return data.user;
  };

  const setSession = (token, u) => { localStorage.setItem("twp_token", token); setUser(u); };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("twp_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, twofaLogin, register, logout, refresh, setUser, setSession }}>
      {children}
    </AuthContext.Provider>
  );
}
